// Per-browser choice of where verification and false alarms surface.
//
// Route by the alert's *status* (pending / cleared / confirmed), not by the
// API's `pending` list: that list also holds just-resolved alarms for a few
// seconds (`is_pending_display`), which is why "alerts page only" still put
// false alarms on the ribbon.

export type AlertSurface = "ribbon" | "alerts" | "off";

export type AlertDisplayPrefs = {
  /** While a detection is still being checked (`status === "pending"`). */
  verification: AlertSurface;
  /** After verification finds no threat (`status === "cleared"`). */
  falsePositive: AlertSurface;
  /** Cleared alerts older than this stay off the ribbon when FP is ribbon. */
  falsePositiveRibbonSinceMs: number;
};

export const DEFAULT_ALERT_DISPLAY_PREFS: AlertDisplayPrefs = {
  verification: "ribbon",
  falsePositive: "alerts",
  falsePositiveRibbonSinceMs: 0,
};

const STORAGE_KEY = "bf-alert-display";

function asSurface(value: unknown, fallback: AlertSurface): AlertSurface {
  return value === "ribbon" || value === "alerts" || value === "off" ? value : fallback;
}

export function readAlertDisplayPrefs(): AlertDisplayPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_ALERT_DISPLAY_PREFS;
    const parsed = JSON.parse(raw) as Partial<AlertDisplayPrefs>;
    return {
      verification: asSurface(parsed.verification, DEFAULT_ALERT_DISPLAY_PREFS.verification),
      falsePositive: asSurface(
        parsed.falsePositive,
        DEFAULT_ALERT_DISPLAY_PREFS.falsePositive,
      ),
      falsePositiveRibbonSinceMs: Number(parsed.falsePositiveRibbonSinceMs) || 0,
    };
  } catch {
    return DEFAULT_ALERT_DISPLAY_PREFS;
  }
}

export function writeAlertDisplayPrefs(prefs: AlertDisplayPrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    window.dispatchEvent(new Event("bf-alert-display"));
  } catch {
    /* private browsing / storage disabled — prefs reset on reload */
  }
}

export function prefsEqual(a: AlertDisplayPrefs, b: AlertDisplayPrefs): boolean {
  return (
    a.verification === b.verification &&
    a.falsePositive === b.falsePositive &&
    a.falsePositiveRibbonSinceMs === b.falsePositiveRibbonSinceMs
  );
}

export function patchAlertDisplayPrefs(
  prev: AlertDisplayPrefs,
  patch: Partial<Pick<AlertDisplayPrefs, "verification" | "falsePositive">>,
): AlertDisplayPrefs {
  const next: AlertDisplayPrefs = { ...prev, ...patch };
  if (patch.falsePositive === "ribbon" && prev.falsePositive !== "ribbon") {
    // Don't resurrect historical false alarms the moment the operator opts in.
    next.falsePositiveRibbonSinceMs = Date.now();
  }
  return next;
}

type RoutedAlert = { status: string; updated_at_ms: number };

export function isVerificationAlert(alert: RoutedAlert): boolean {
  return alert.status === "pending";
}

export function showOnRibbon(alert: RoutedAlert, prefs: AlertDisplayPrefs): boolean {
  // Pending is verification — never fall through to the confirmed "always show"
  // path, including when a stale tab still has verification === "ribbon" in
  // memory. Callers must pass the latest prefs from localStorage.
  if (isVerificationAlert(alert)) return prefs.verification === "ribbon";
  if (alert.status === "cleared") {
    if (alert.updated_at_ms < prefs.falsePositiveRibbonSinceMs) return false;
    return prefs.falsePositive === "ribbon";
  }
  if (alert.status === "confirmed") return true;
  return false;
}

export function showOnAlertsPage(alert: RoutedAlert, prefs: AlertDisplayPrefs): boolean {
  if (isVerificationAlert(alert)) return prefs.verification === "alerts";
  if (alert.status === "cleared") return prefs.falsePositive === "alerts";
  if (alert.status === "confirmed") return true;
  return false;
}
